import sidebarStyles from "./sidebar.module.css";
import { AppImage } from "../appMedia";
import { Category } from "../../types/types";
import React, { FC } from "react";
import useMediaQuery from "../../hooks/useMediaQuery";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAppSelector } from "../../services/hooks";
import defaultAvatar from "../../images/default_avatar.png";
import { mobileWidth } from "../../utils/constants";
import { userSelector } from "../../services/selectors/user.selectors";
import { useActions } from "../../services/hooks/useActions";
import { tWithKeyAttr as t } from "../../i18nHelper";

export interface ISidebar {
  changeCategory: (
    e:
      | React.MouseEvent<HTMLLIElement, MouseEvent>
      | React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    cat: Category
  ) => void;
  activeCategory: Category;
  toggleMenu: () => void;
  openMenu: boolean;
}
const Sidebar: FC<ISidebar> = ({
  changeCategory,
  activeCategory,
  toggleMenu,
  openMenu,
}) => {
  const mobile = useMediaQuery(mobileWidth);
  const location = useLocation();
  const navigate = useNavigate();
  const isChat = location.pathname === "/chat";
  const user = useAppSelector(userSelector);

  // const isTestUser =
  //   user?.email?.toLowerCase() === testUserEmail ||
  //   user?.email?.toLowerCase() === testUserEmail2;

  const { logout } = useActions();

  return (
    <section
      className={`${sidebarStyles.sidebar} ${
        isChat && !mobile && sidebarStyles.sidebar_chat
      } ${mobile && openMenu ? sidebarStyles.sidebar_open : ""}`}
      onClick={mobile ? toggleMenu : undefined}
    >
      <div
        className={`${sidebarStyles.sidebar__content_visible} ${
          openMenu && !mobile
            ? ""
            : mobile
            ? sidebarStyles.sidebar_open
            : sidebarStyles.sidebar__content_small
        }`}
      >
        <button className={sidebarStyles.sidebar__arrow} onClick={toggleMenu} />
        <div className={sidebarStyles.sidebar__logo} />
        <div className={sidebarStyles.sidebar__welcome} onClick={(e) => e.stopPropagation()}>
          <h2 className={sidebarStyles.sidebar__title} data-testid="user-name-sidebar">
            {t("HI")} {user.firstName ? user.firstName : t("GUEST")}
          </h2>
          <AppImage
            onClick={(e) => {
              !mobile && e.stopPropagation();
              navigate("/profile", {
                replace: true,
                state: { pathname: "profile" },
              });
            }}
            className={sidebarStyles.sidebar__avatar}
            src={user.avatar || defaultAvatar}
            alt={t("USER_AVATAR_ALT")}
          />
        </div>
        <div className={sidebarStyles.scrollable} onClick={(e) => {!mobile && e.stopPropagation()}}>
          <ul className={sidebarStyles.sidebar__pages}>
            <li
              className={`${sidebarStyles.sidebar__page} ${
                activeCategory === Category.Day
                  ? sidebarStyles.sidebar__page_active
                  : ""
              }`}
              onClick={(e) => changeCategory(e, Category.Day)}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>{t("MY_DAY")}</p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_myDay}`}
              />
            </li>
            <li
              className={`${sidebarStyles.sidebar__page} ${
                activeCategory === Category.Treatment
                  ? sidebarStyles.sidebar__page_active
                  : ""
              } `}
              onClick={(e) => changeCategory(e, Category.Treatment)}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>
                {t("MY_TREATMENT")}
              </p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_treatment}`}
              />
            </li>
            <li
              className={`${sidebarStyles.sidebar__page} ${
                activeCategory === Category.Help
                  ? sidebarStyles.sidebar__page_active
                  : ""
              }`}
              onClick={(e) => changeCategory(e, Category.Help)}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>
                {t("RECEIVING_HELP")}
              </p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_support}`}
              />
            </li>
            <li
              className={`${sidebarStyles.sidebar__page} ${
                activeCategory === Category.Goals
                  ? sidebarStyles.sidebar__page_active
                  : ""
              }`}
              onClick={(e) => changeCategory(e, Category.Goals)}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>
                {t("INSIGHTS_AND_GOALS")}
              </p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_goals}`}
              />
            </li>
            <li
              className={`${sidebarStyles.sidebar__page} ${
                activeCategory === Category.Profile
                  ? sidebarStyles.sidebar__page_active
                  : ""
              }`}
              onClick={(e) => changeCategory(e, Category.Profile)}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>
                {t("MY_PROFILE")}
              </p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_profile}`}
              />
            </li>
            <li>
              <Link
                to={"/chat"}
                className={`${sidebarStyles.sidebar__page} 
                ${sidebarStyles.sidebar__page_messages}
                ${
                  activeCategory === Category.Messages
                    ? sidebarStyles.sidebar__page_active
                    : ""
                }`}
                data-testid="messages-button"
              >
                {user.unreadMessages > 0 && (
                  <div className={sidebarStyles.sidebar__messages}>
                    {user.unreadMessages}
                  </div>
                )}
                <p className={sidebarStyles.sidebar__pageTitle}>
                  {t("MESSAGES")}
                </p>
                <div
                  className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_messages}`}
                />
              </Link>
            </li>
            <Link
              to={"/prevencher"}
              state={{ pathname: "chat" }}
              className={`${sidebarStyles.sidebar__page} ${
                activeCategory === Category.Prevencher
                  ? sidebarStyles.sidebar__page_active
                  : ""
              }`}
              onClick={(e) => changeCategory(e, Category.Prevencher)}
            >
              <p className={sidebarStyles.sidebar__pageTitle}>
                {t("TITLE_PREVENCHER")}
              </p>
              <div
                className={`${sidebarStyles.sidebar__pageIcon} ${sidebarStyles.sidebar__pageIcon_prevencher} `}
              />
            </Link>
          </ul>
          <ul className={sidebarStyles.sidebar__footer}>
            <li
              onClick={(e) => {
                // if (isDisabled) {
                //   e.preventDefault();
                //   e.stopPropagation();
                //   return;
                // }
                changeCategory(e, Category.Contacts);
              }}
            >
              <div
                className={`${sidebarStyles.sidebar__footerItem} ${
                  activeCategory === Category.Contacts &&
                  sidebarStyles.sidebar__page_active
                }`}
              >
                <p className={sidebarStyles.sidebar__footerTitle}>
                  {t("TECHNICAL_SUPPORT")}
                </p>
                <div
                  className={`${sidebarStyles.sidebar__footerIcon} ${sidebarStyles.sidebar__footerIcon_contacts}`}
                />
              </div>
            </li>
            <li
              onClick={(e) => {
                changeCategory(e, Category.Terms);
                navigate("/terms", {
                  replace: true,
                });
              }}
            >
              <div
                className={`${sidebarStyles.sidebar__footerItem} ${
                  activeCategory === Category.Terms &&
                  sidebarStyles.sidebar__page_active
                }`}
              >
                <p className={sidebarStyles.sidebar__footerTitle}>
                  {t("POLICY_AND_PRIVACY")}
                </p>
                <div
                  className={`${sidebarStyles.sidebar__footerIcon} ${sidebarStyles.sidebar__footerIcon_conditions}`}
                />
              </div>
            </li>

            <li>
              <div
                className={sidebarStyles.sidebar__footerItem}
                onClick={(e) => {
                  e.stopPropagation();
                  logout();
                  navigate("/login");
                }}
              >
                <p className={sidebarStyles.sidebar__footerTitle}>
                  {t("EXIT")}
                </p>
                <div
                  className={`${sidebarStyles.sidebar__footerIcon} ${sidebarStyles.sidebar__footerIcon_logout}`}
                />
              </div>
            </li>
          </ul>
        </div>
      </div>
    </section>
  );
};

export default Sidebar;
